import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Navbar,
  Nav,
  Container,
  Button,
  Row,
  Col,
  Card,
  Badge,
} from "react-bootstrap";
import { BsShieldCheck, BsPeople, BsFileEarmarkText, BsMoon, BsSun } from "react-icons/bs";
import { GoLaw } from "react-icons/go";

export default function AboutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Theme
  const [theme, setTheme] = useState("light");
  useEffect(() => {
    document.documentElement.setAttribute("data-bs-theme", theme);
  }, [theme]);
  const toggleTheme = () => setTheme((t) => (t === "light" ? "dark" : "light"));

  // Sticky shadow when pinned
  const [pinned, setPinned] = useState(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => setPinned(entries[0].intersectionRatio < 1),
      { rootMargin: "0px", threshold: [1] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Animate on Scroll
  useEffect(() => {
    const items = document.querySelectorAll(".reveal");
    const aos = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            aos.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );
    items.forEach((el) => aos.observe(el));
    return () => aos.disconnect();
  }, []);

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", fontFamily: "Prompt, sans-serif", backgroundColor: "#f8f9fa"}}>
      <style>{`
        :root{
          --e1: 700ms;
          --delay: 0ms;
          --rise: 16px;
          --ease: cubic-bezier(.22,.61,.36,1);
        }
        @media (prefers-reduced-motion: reduce) { *{ animation:none !important; transition:none !important; } }

        /* ===== Reveal ===== */
        .reveal{ opacity:0; transform:translateY(var(--rise)); filter:blur(2px);
          transition: opacity var(--e1) var(--ease) var(--delay),
                      transform var(--e1) var(--ease) var(--delay),
                      filter var(--e1) var(--ease) var(--delay); }
        .reveal.is-visible{ opacity:1; transform:none; filter:blur(0); }
        .slide-left{ transform:translateX(18px); }
        .slide-right{ transform:translateX(-18px); }
        .zoom-in{ transform:scale(.98); }


        /* ===== Hero band that visually bridges from video hero ===== */
        .about-bridge{
          position: relative;
          padding: clamp(56px, 8vw, 88px) 0;
          color: #fff;
          border-radius: 0 0 28px 28px; /* ขอบโค้งล่างให้ต่อเนื่อง*/
          overflow: hidden;
          isolation: isolate;
        }
        /* เบลอไฟโบเก้สังเคราะห์ให้ mood ใกล้เคียงหน้าแรก */
        .about-bridge::before{
          content:"";
          position:absolute; inset:0;
          background: #262626;
          backdrop-filter: blur(20px);
          border-top: none; 
          border-bottom: 1px solid rgba(255,255,255,0.05);
          z-index:-1;
        }
        .about-bridge .container{ position:relative; }

        /* ===== Section cards / spacing align to home scale ===== */
        .section-title{ font-weight:800; letter-spacing:.2px; }
        .card-hover{ transition:transform 300ms var(--ease), box-shadow 300ms var(--ease); }
        .card-hover:hover{ transform:translateY(-4px); box-shadow:0 1rem 2rem rgba(0,0,0,.08)!important; }
        .stagger > *:nth-child(1){ --delay:60ms } .stagger > *:nth-child(2){ --delay:120ms }
        .stagger > *:nth-child(3){ --delay:180ms } .stagger > *:nth-child(4){ --delay:240ms }

        /* Button ripple-lite */
        .btn-ripple{ position:relative; overflow:hidden; }
        .btn-ripple::after{
          content:""; position:absolute; inset:0; opacity:0;
          background: radial-gradient(120px 120px at var(--x,50%) var(--y,50%), rgba(255,255,255,.35), transparent 40%);
          transition:opacity 400ms var(--ease);
        }
        .btn-ripple:active::after{ opacity:1; transition:opacity 0ms; }
      `}</style>

      {/* Sentinel for navbar state */}
      <div ref={sentinelRef} style={{ position: "absolute", top: 0, height: 0, zIndex: 1029 }} />

      
      <main style={{ flexGrow: 1 }}>
        {/* Bridge band (โทนเข้ม/ตัวอักษรขาว ให้ความต่อเนื่องจากวิดีโอฮีโร่) */}
        <section className="about-bridge">
          <Container className="py-5">
            <Badge bg="light" text="dark" className="px-3 py-2 rounded-pill reveal" style={{ "--delay":"40ms" }}>
              {t("about.badge")}
            </Badge>
            <h1 className="fw-black lh-sm mt-3 reveal" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", "--delay":"100ms" }}>
              {t("about.hero_title")}
            </h1>
            <p className="mt-3 fs-5 reveal" style={{ maxWidth: 900, "--delay":"160ms", opacity:.95 }}>
              {t("about.hero_desc")}
            </p>
            
          </Container>
        </section>

        {/* Content sections (พื้นหลังสว่าง อ่านง่าย ต่อเนื่องจากโทนเข้มด้านบน) */}
        <Container className="py-5 py-md-6">

          {/* Strengths */}
          <section id="strengths" className="mt-2" >
            <h2 className="text-center section-title reveal" style={{ fontSize: "clamp(1.6rem, 2.5vw, 2rem)" }}>
              {t("about.strengths_title")}
            </h2>
            <p className="text-center text-secondary mx-auto reveal" style={{ maxWidth: 760, "--delay":"80ms" }}>
              {t("about.strengths_subtitle")}
            </p>

            <Row className="g-4 mt-1 stagger">
              <Col md={4}>
                <Card className="h-100 shadow-sm card-hover border-0 reveal" style={{ backgroundColor: "#fff" }}>
                  <Card.Body className="text-center p-4">
                    <div className="display-6 mb-2 text-dark"><GoLaw /></div>
                    <Card.Title className="fw-bold text-dark">{t("about.strength_1_title")}</Card.Title>
                    <Card.Text className="text-secondary" style={{ fontSize: "0.95rem" }}>
                      {t("about.strength_1_desc")}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="h-100 shadow-sm card-hover border-0 reveal" style={{ "--delay":"80ms", backgroundColor: "#fff" }}>
                  <Card.Body className="text-center p-4">
                    <div className="display-6 mb-2 text-dark"><BsFileEarmarkText /></div>
                    <Card.Title className="fw-bold text-dark">{t("about.strength_2_title")}</Card.Title>
                    <Card.Text className="text-secondary" style={{ fontSize: "0.95rem" }}>
                      {t("about.strength_2_desc")}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="h-100 shadow-sm card-hover border-0 reveal" style={{ "--delay":"160ms", backgroundColor: "#fff" }}>
                  <Card.Body className="text-center p-4">
                    <div className="display-6 mb-2 text-dark"><BsPeople /></div>
                    <Card.Title className="fw-bold text-dark">{t("about.strength_3_title")}</Card.Title>
                    <Card.Text className="text-secondary" style={{ fontSize: "0.95rem" }}>
                      {t("about.strength_3_desc")}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <div className="text-center mt-4 reveal" style={{ "--delay":"220ms" }}>
              <span className="text-secondary small">
                <BsShieldCheck className="me-1" />
                {t("about.transparency")}
              </span>
            </div>
          </section>

          {/* Services */}
          <section id="services" className="mt-5">
            <h2 className="text-center section-title reveal" style={{ fontSize: "clamp(1.6rem, 2.5vw, 2rem)" }}>
              {t("about.special_services_title")}
            </h2>
            <Row className="g-4 mt-1">
              <Col md={6}>
                <Card className="h-100 shadow-sm border-0 card-hover reveal slide-right" style={{ backgroundColor: "#fff" }}>
                  <Card.Body className="p-4">
                    <Card.Title className="fs-4 fw-bold text-dark">{t("about.service_1_title")}</Card.Title>
                    <Card.Text className="text-secondary">
                      {t("about.service_1_desc")}
                    </Card.Text>
                    <Button 
                      className="btn-ripple" 
                      variant="dark" 
                      size="sm" 
                      style={{ borderRadius: "50px", padding: "8px 25px" }}
                      onClick={() => navigate("/special-ops")}
                    >
                      {t("about.read_more")}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="h-100 shadow-sm border-0 card-hover reveal slide-left" style={{ backgroundColor: "#fff" }}>
                  <Card.Body className="p-4">
                    <Card.Title className="fs-4 fw-bold text-dark">{t("about.service_2_title")}</Card.Title>
                    <Card.Text className="text-secondary">
                      {t("about.service_2_desc")}
                    </Card.Text>
                    <Button 
                      className="btn-ripple" 
                      variant="dark" 
                      size="sm" 
                      style={{ borderRadius: "50px", padding: "8px 25px" }}
                      onClick={() => navigate("/special-ops")}
                    >
                      {t("about.read_more")}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </section>

          
        </Container>
      </main>

      {/* Ripple pointer position */}
      <script
        dangerouslySetInnerHTML={{
          __html: `
          document.addEventListener('pointerdown', (e) => {
            const t = e.target.closest('.btn-ripple');
            if(!t) return;
            const r = t.getBoundingClientRect();
            t.style.setProperty('--x', (e.clientX - r.left)+'px');
            t.style.setProperty('--y', (e.clientY - r.top)+'px');
          });
        `,
        }}
      />
    </div>
  );
}
